import React from 'react';
import { useQuery } from 'react-query';
import api from '../services/api';
import './PaymentDetailsHistory.css';

const PaymentDetailsHistory = ({ paymentId, totalAmount, paidAmount }) => {
  const { data: details, isLoading, error } = useQuery(
    ['payment-details', paymentId],
    async () => {
      const response = await api.get(`/payments/${paymentId}/details`);
      return response.data;
    },
    { enabled: !!paymentId }
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return 'S/ 0.00';
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(num);
  };

  const getMethodLabel = (method) => {
    const methods = {
      'cash': '💵 Efectivo',
      'card': '💳 Tarjeta',
      'transfer': '🏦 Transferencia',
      'yape': '📱 Yape',
      'plin': '📱 Plin',
      'other': '📝 Otro',
    };
    return methods[method] || method || '-';
  };

  const remaining = parseFloat(totalAmount || 0) - parseFloat(paidAmount || 0);

  if (isLoading) {
    return <div className="pdh-loading">Cargando historial...</div>;
  }

  if (error) {
    return <div className="pdh-error">Error al cargar el historial de pagos.</div>;
  }

  return (
    <div className="pdh-container">
      <div className="pdh-summary">
        <div className="pdh-summary-item">
          <span className="pdh-summary-label">Monto Total</span>
          <span className="pdh-summary-value">{formatCurrency(totalAmount)}</span>
        </div>
        <div className="pdh-summary-item">
          <span className="pdh-summary-label">Total Pagado</span>
          <span className="pdh-summary-value pdh-paid">{formatCurrency(paidAmount)}</span>
        </div>
        <div className="pdh-summary-item">
          <span className="pdh-summary-label">Pendiente</span>
          <span className={`pdh-summary-value ${remaining > 0 ? 'pdh-pending' : 'pdh-complete'}`}>
            {formatCurrency(remaining)}
          </span>
        </div>
      </div>

      {details && details.length > 0 ? (
        <div className="pdh-table-wrapper">
          <table className="pdh-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Fecha</th>
                <th>Monto</th>
                <th>Método</th>
                <th>Referencia</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {details.map((detail, index) => (
                <tr key={detail.id}>
                  <td className="pdh-index">{index + 1}</td>
                  <td>{formatDate(detail.payment_date)}</td>
                  <td className="pdh-amount">{formatCurrency(detail.amount)}</td>
                  <td>{getMethodLabel(detail.payment_method)}</td>
                  <td>{detail.reference_number || '-'}</td>
                  <td className="pdh-notes">{detail.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="2"><strong>Total Abonado</strong></td>
                <td className="pdh-amount">
                  <strong>
                    {formatCurrency(details.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0))}
                  </strong>
                </td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="pdh-empty">
          No hay registros de abonos para este pago.
        </div>
      )}
    </div>
  );
};

export default PaymentDetailsHistory;
